/**
 * paletteDnd.ts — HTML5 drag-and-drop glue for dragging palette items into the
 * canvas. Two payloads, distinguished by custom dataTransfer MIME types:
 *   - a block type    → dropped on the canvas, inserts a new block at the position
 *   - a variable      → dropped on a text field, inserts an atomic var-chip there
 *
 * Click-to-insert (the original interaction) is kept; drag is additive.
 */
import { BlockType } from "../blockModel/types";
import { EmailBuilderActions } from "./useEmailBuilder";
import { unchip, decodeEntities } from "./chipDom";
import { writeValueToEl } from "./writeBack";

export const MIME_BLOCK = "application/x-wl-block";
export const MIME_VAR = "application/x-wl-var";
/** Dragging a "section title" from the palette INTO a summary block, at a row position. */
export const MIME_SECTION = "application/x-wl-section";
/** Plain-text fallback MIME so the drag has a textual representation too. */
export const MIME_TEXT = "text/plain";

/** Encode a variable payload as "name|label" (label may contain no newline). */
export function encodeVar(name: string, label: string): string {
	return `${name}|${label}`;
}

export function decodeVar(raw: string): { name: string; label: string } | null {
	if (!raw) return null;
	const i = raw.indexOf("|");
	if (i < 0) return { name: raw, label: raw };
	return { name: raw.slice(0, i), label: raw.slice(i + 1) };
}

/**
 * Set a clean, compact drag image (a coloured pill with the label) so the user can
 * clearly see what they're dragging — the default ghost of the whole palette row is
 * faint and cluttered ("kéo rất khó nhìn").
 */
export function setDragImagePill(e: { dataTransfer: DataTransfer }, label: string, color = "#005e9e"): void {
	try {
		const ghost = document.createElement("div");
		ghost.textContent = label;
		ghost.style.cssText =
			"position:fixed;top:-1000px;left:-1000px;padding:7px 14px;border-radius:9px;" +
			"font:600 13px -apple-system,Segoe UI,Roboto,sans-serif;white-space:nowrap;" +
			"box-shadow:0 4px 14px rgba(15,23,42,.28);pointer-events:none;z-index:99999;" +
			`background:${color};color:#fff;`;
		ghost.setAttribute("data-drag-ghost", "1");
		document.body.appendChild(ghost);
		e.dataTransfer.setDragImage(ghost, 12, 16);
		// Remove on the next tick (after the browser snapshots the image).
		window.setTimeout(() => {
			if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
		}, 0);
	} catch (_) {
		// setDragImage unsupported — fall back to the default ghost.
	}
}

/**
 * Given the pointer Y and the list of block elements (each carrying data-id),
 * return the id of the block the new block should be inserted BEFORE, or null
 * to append at the end (before footer). Mirrors useBlockDrag's drop math.
 */
export function computeDropBeforeId(clientY: number, els: ArrayLike<Element>): string | null {
	for (let i = 0; i < els.length; i++) {
		const el = els[i] as HTMLElement;
		const rect = el.getBoundingClientRect();
		if (clientY < rect.top + rect.height / 2) {
			return el.dataset.id ?? null;
		}
	}
	return null;
}

/** Find the nearest ancestor-or-self contentEditable element. */
function findEditable(el: Element | null): HTMLElement | null {
	while (el) {
		if ((el as HTMLElement).isContentEditable) return el as HTMLElement;
		el = el.parentElement;
	}
	return null;
}

/**
 * Insert a var-chip into the editable text field located at (x, y), at the caret
 * nearest the drop point. Returns true if a chip was inserted.
 */
export function insertVarChipAtPoint(
	x: number,
	y: number,
	targetEl: EventTarget | null,
	name: string,
	label: string,
	actions: EmailBuilderActions
): boolean {
	// Prefer the actual drop target (the field dropped on); elementFromPoint can resolve to
	// a wrapping div whose editable child findEditable (walks UP) would miss.
	const editable = findEditable(targetEl as Element | null) || findEditable(document.elementFromPoint(x, y));
	if (!editable || !editable.getAttribute("data-el")) return false;

	const blockId = editable.getAttribute("data-el")!.split(":")[0] || null;
	if (!blockId) return false;

	// Do NOT mutate the contentEditable DOM (React owns its children — direct insertion
	// desyncs the vDOM → "removeChild" crash). Append the ${var} to the field's value and
	// let React re-render the chip from state.
	void label;
	const current = decodeEntities(unchip(editable.innerHTML)).replace(/\s+$/, "");
	const newValue = (current ? current + " " : "") + "${" + name + "}";
	writeValueToEl(editable, blockId, newValue, actions);
	return true;
}

/** Read a block-type payload off a drop event, if present. */
export function readBlockPayload(dt: DataTransfer | null): BlockType | null {
	if (!dt) return null;
	const t = dt.getData(MIME_BLOCK);
	return t ? (t as BlockType) : null;
}

/** Read a variable payload off a drop event, if present. */
export function readVarPayload(dt: DataTransfer | null): { name: string; label: string } | null {
	if (!dt) return null;
	const raw = dt.getData(MIME_VAR);
	return raw ? decodeVar(raw) : null;
}

/** True if the drag carries a "section title" payload (drop INTO a summary block). */
export function isSectionDrag(dt: DataTransfer | null): boolean {
	if (!dt) return false;
	// dragover exposes only `types`; drop also has getData — check both.
	return Array.from(dt.types).includes(MIME_SECTION) || !!dt.getData(MIME_SECTION);
}

/** Where a dropped section title should land: which summary block + row index. */
export interface SectionDropTarget {
	blockId: string;
	index: number;
	/** Y offset (px) of the insertion line, relative to `emailEl`. */
	top: number;
}

/**
 * Resolve the summary block under (x, y) and the row index the section title should
 * be inserted at. Returns null when the cursor is not over a block's row area.
 * The caller MUST verify blockId maps to a summary block (only summaries take titles).
 */
export function computeSectionDrop(x: number, y: number, emailEl: HTMLElement | null): SectionDropTarget | null {
	if (!emailEl) return null;
	// Walk up from the drop point to the nearest [data-id] block element.
	let el = document.elementFromPoint(x, y) as HTMLElement | null;
	let blockEl: HTMLElement | null = null;
	while (el && el !== emailEl) {
		if (el.hasAttribute("data-id")) {
			blockEl = el;
			break;
		}
		el = el.parentElement;
	}
	if (!blockEl) return null;

	const blockId = blockEl.getAttribute("data-id")!;
	const emailRect = emailEl.getBoundingClientRect();
	const rows = Array.from(blockEl.querySelectorAll(`[data-row^="${blockId}:"]`)) as HTMLElement[];

	// Empty summary → drop at the top (index 0).
	if (rows.length === 0) {
		return { blockId, index: 0, top: blockEl.getBoundingClientRect().top - emailRect.top };
	}

	for (const r of rows) {
		const rect = r.getBoundingClientRect();
		if (y < rect.top + rect.height / 2) {
			const idx = parseInt((r.getAttribute("data-row") || "").split(":")[1], 10);
			return { blockId, index: isNaN(idx) ? 0 : idx, top: rect.top - emailRect.top };
		}
	}

	// Past the last row → append after it.
	const last = rows[rows.length - 1];
	const lastIdx = parseInt((last.getAttribute("data-row") || "").split(":")[1], 10);
	return {
		blockId,
		index: isNaN(lastIdx) ? rows.length : lastIdx + 1,
		top: last.getBoundingClientRect().bottom - emailRect.top,
	};
}
